import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { MdOutlineSearch, MdOutlinePlace, MdMyLocation, MdOutlineClose } from 'react-icons/md';
import { formatMapboxFeatureAddress, parseCoordinateText } from './format';

const RADIUS_OPTIONS = [1, 2, 3, 5, 8, 10, 12];
const PLACES_RADIUS_OPTIONS = [0.5, 1, 2, 3, 5];
const MIN_QUERY = 3;
const DEBOUNCE_MS = 280;

async function geocodeSuggestions(query, signal) {
  const token = process.env.REACT_APP_MAPBOX_TOKEN || mapboxgl.accessToken;
  if (!token) return [];
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
    + `?access_token=${token}&autocomplete=true&country=us&limit=6&types=address,place,postcode,locality,neighborhood,poi`;
  const res = await fetch(url, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data?.features) ? data.features : [];
}

/**
 * Address / coordinate input with Mapbox autocomplete. Submits either
 * `{ address }` or `{ coordinates: {latitude, longitude} }`.
 */
export default function LandSearchBar({
  mode,
  onModeChange,
  value,
  onValueChange,
  radius,
  onRadiusChange,
  placesRadius,
  onPlacesRadiusChange,
  compsRadius,
  onCompsRadiusChange,
  onSubmit,
  loading,
  onPreview,
  radiusDirty,
  placesRadiusDirty,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [validation, setValidation] = useState('');
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const skipNextLookupRef = useRef(false);
  const submittedRef = useRef(false);

  const closeSuggestions = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  // Debounced autocomplete — only for values the user is typing (a focused
  // input); programmatic fills (examples, pin drops, restored sessions) never
  // open the dropdown.
  useEffect(() => {
    if (mode !== 'address') { setSuggestions([]); return undefined; }
    if (skipNextLookupRef.current) { skipNextLookupRef.current = false; return undefined; }
    if (typeof document !== 'undefined' && document.activeElement !== inputRef.current) { return undefined; }
    const q = (value || '').trim();
    if (q.length < MIN_QUERY || parseCoordinateText(q)) { setSuggestions([]); closeSuggestions(); return undefined; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const features = await geocodeSuggestions(q, controller.signal);
        // A response landing after Enter must not reopen the list over the results.
        if (controller.signal.aborted || submittedRef.current) return;
        setSuggestions(features);
        setOpen(features.length > 0);
        setActiveIndex(-1);
      } catch (err) {
        if (err?.name !== 'AbortError') { setSuggestions([]); closeSuggestions(); }
      }
    }, DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, mode, closeSuggestions]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  // A submission always closes the dropdown.
  useEffect(() => { if (loading) closeSuggestions(); }, [loading, closeSuggestions]);

  const choose = (feature) => {
    const formatted = formatMapboxFeatureAddress(feature);
    skipNextLookupRef.current = true;
    onValueChange(formatted);
    closeSuggestions();
    setSuggestions([]);
    if (onPreview && Array.isArray(feature.center) && feature.center.length === 2) {
      onPreview({ longitude: feature.center[0], latitude: feature.center[1] });
    }
    inputRef.current?.focus();
  };

  const submit = () => {
    const raw = (value || '').trim();
    setValidation('');
    submittedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    setSuggestions([]);
    if (!raw) { setValidation(mode === 'address' ? 'Enter an address to evaluate.' : 'Enter a latitude, longitude pair.'); return; }
    const coords = parseCoordinateText(raw);
    if (mode === 'coordinates') {
      if (!coords) { setValidation('Use decimal degrees: latitude, longitude (e.g. 40.1320, -75.1501).'); return; }
      closeSuggestions();
      onSubmit({ coordinates: coords });
      return;
    }
    closeSuggestions();
    if (coords) { onSubmit({ coordinates: coords }); return; }
    if (raw.length < 5) { setValidation('That address is too short. Include street, city and state.'); return; }
    onSubmit({ address: raw });
  };

  const onKeyDown = (event) => {
    if (open && suggestions.length) {
      if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((i) => (i + 1) % suggestions.length); return; }
      if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); return; }
      if (event.key === 'Escape') { event.preventDefault(); closeSuggestions(); return; }
      if (event.key === 'Enter' && activeIndex >= 0) { event.preventDefault(); choose(suggestions[activeIndex]); return; }
    }
    if (event.key === 'Enter') { event.preventDefault(); submit(); }
  };

  const placeholder = mode === 'address'
    ? 'Street address, city, state ZIP — e.g. 13 N Riverview Ave, Edison, NJ 08817'
    : 'Latitude, longitude — e.g. 40.5012, -74.4101';

  return (
    <div className="le-search">
      <div className="le-segmented" role="tablist" aria-label="Location input mode">
        <button type="button" role="tab" aria-selected={mode === 'address'} className={mode === 'address' ? 'active' : ''} onClick={() => { onModeChange('address'); setValidation(''); }}>
          <MdOutlinePlace size={15} /> Address
        </button>
        <button type="button" role="tab" aria-selected={mode === 'coordinates'} className={mode === 'coordinates' ? 'active' : ''} onClick={() => { onModeChange('coordinates'); setValidation(''); closeSuggestions(); }}>
          <MdMyLocation size={15} /> Coordinates
        </button>
      </div>

      <div className="le-search-field" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) closeSuggestions(); }}>
        <MdOutlineSearch size={18} className="le-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="le-search-input"
          value={value}
          placeholder={placeholder}
          onChange={(e) => { submittedRef.current = false; onValueChange(e.target.value); setValidation(''); }}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls="le-search-listbox"
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          disabled={loading}
        />
        {value ? (
          <button type="button" className="le-search-clear" onClick={() => { onValueChange(''); closeSuggestions(); inputRef.current?.focus(); }} aria-label="Clear" tabIndex={-1}>
            <MdOutlineClose size={16} />
          </button>
        ) : null}
        {open && suggestions.length > 0 ? (
          <ul id="le-search-listbox" className="le-search-suggestions" role="listbox">
            {suggestions.map((feature, i) => {
              const formatted = formatMapboxFeatureAddress(feature);
              const secondary = (feature.place_name || '').replace(/,\s*United States$/i, '');
              return (
                <li key={feature.id || i} role="option" aria-selected={i === activeIndex}>
                  <button
                    type="button"
                    className={`le-search-suggestion ${i === activeIndex ? 'active' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(feature)}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    <MdOutlinePlace size={16} />
                    <span className="le-search-suggestion-text">
                      <span className="le-search-suggestion-primary">{formatted}</span>
                      {secondary && secondary !== formatted ? <span className="le-search-suggestion-secondary">{secondary}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <label className={`le-radius ${radiusDirty ? 'le-radius-dirty' : ''}`} title="Demographics radius (census tracts intersecting this circle)">
        <span className="le-radius-label">Demographics</span>
        <select value={radius} onChange={(e) => onRadiusChange(Number(e.target.value))} disabled={loading}>
          {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} mi</option>)}
        </select>
      </label>

      <label className={`le-radius ${placesRadiusDirty ? 'le-radius-dirty' : ''}`} title="Nearby places radius (anchor brands and general amenities; externalities always scan 5 mi)">
        <span className="le-radius-label">Places</span>
        <select value={placesRadius} onChange={(e) => onPlacesRadiusChange(Number(e.target.value))} disabled={loading}>
          {PLACES_RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} mi</option>)}
        </select>
      </label>

      <label className="le-radius" title="Sale-comps radius (nearby single-family listings)">
        <span className="le-radius-label">Comps</span>
        <select value={compsRadius} onChange={(e) => onCompsRadiusChange(Number(e.target.value))} disabled={loading}>
          {[0.5, 1, 2, 3, 5].map((r) => <option key={r} value={r}>{r} mi</option>)}
        </select>
      </label>

      <button type="button" className="le-evaluate-btn" onClick={submit} disabled={loading}>
        {loading ? <span className="le-spinner" aria-hidden="true" /> : null}
        {loading ? 'Evaluating…' : 'Evaluate'}
      </button>

      {validation ? <div className="le-search-validation" role="alert">{validation}</div> : null}
    </div>
  );
}
